import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { positionBadge, StageStepper } from "@/lib/bills";
import { ArrowLeft, ArrowRight, Users } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { InlineAssistant } from "@/components/AssistantWidget";

export default function PriorityDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/public/priorities/${slug}`, { params: { lang } })
       .then(r => setData(r.data))
       .catch(() => setData(false));
  }, [slug, lang]);

  if (data === false) return <div className="max-w-4xl mx-auto px-6 py-24 font-serif text-2xl">Priority not found.</div>;
  if (!data) return <div className="max-w-4xl mx-auto px-6 py-24">Loading…</div>;

  const { priority: p, bills, alerts, statements } = data;

  return (
    <div data-testid="priority-detail">
      <section className="bg-ccc-plum text-ccc-ivory py-20">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/#ten-priorities" className="text-ccc-gold text-sm inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={14}/> Ten Priorities
          </Link>
          <div className="flex items-baseline gap-6 mb-6">
            <div className="font-serif text-7xl text-ccc-gold shrink-0">{p.order}</div>
            <div>
              <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-2">Ten Catholic Priorities</div>
              <h1 className="font-serif text-5xl leading-tight">{p.title}</h1>
            </div>
          </div>
          <p className="text-stone-300 text-lg leading-relaxed max-w-3xl italic">{p.summary}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-14">
        <p className="text-lg leading-relaxed text-ccc-charcoal">{p.detail}</p>
        <div className="gilded-divider my-10"/>
      </section>

      {alerts.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-14" data-testid="priority-alerts">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-3xl text-ccc-plum">Take action now.</h2>
            <Link to="/take-action/action-alerts" className="text-sm text-ccc-purple uppercase tracking-wider font-semibold inline-flex items-center gap-1">
              All alerts <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {alerts.map(a => (
              <Link key={a.id} to={`/take-action/action-alerts/${a.slug}`}
                className="bg-white border border-stone-200 p-6 hover:border-ccc-purple transition-colors group">
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${positionBadge(a.position)}`}>{a.position}</span>
                  <div className="text-xs text-stone-500 inline-flex items-center gap-1"><Users size={12}/> {a.actions_count}</div>
                </div>
                <h3 className="font-serif text-xl text-ccc-plum group-hover:text-ccc-purple leading-tight">{a.title}</h3>
                <p className="text-sm text-stone-600 mt-2 line-clamp-2">{a.summary}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {bills.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-14" data-testid="priority-bills">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-3xl text-ccc-plum">Bills we're tracking.</h2>
            <Link to="/legislative-work" className="text-sm text-ccc-purple uppercase tracking-wider font-semibold inline-flex items-center gap-1">
              Full tracker <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {bills.map(b => (
              <Link key={b.id} to={`/legislative-work/${b.id}`}
                className="bg-white border border-stone-200 p-6 hover:border-ccc-purple transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${positionBadge(b.position)}`}>{b.position}</span>
                  <span className="font-mono text-xs text-stone-500">{b.number}</span>
                </div>
                <div className="font-serif text-lg text-ccc-plum mb-3 leading-tight">{b.title}</div>
                <StageStepper stage={b.stage}/>
              </Link>
            ))}
          </div>
        </section>
      )}

      {statements.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="priority-statements">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-3xl text-ccc-plum">Statements &amp; testimony.</h2>
            <Link to="/resources/statements" className="text-sm text-ccc-purple uppercase tracking-wider font-semibold inline-flex items-center gap-1">
              Full library <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {statements.map(s => (
              <Link key={s.id} to={`/resources/statements/${s.slug}`}
                className="bg-white border border-stone-200 p-6 hover:border-ccc-purple transition-colors">
                <div className="text-xs uppercase tracking-wider text-ccc-gold-dark mb-2">{s.type} &middot; {s.published_at}</div>
                <div className="font-serif text-xl text-ccc-plum leading-tight mb-2">{s.title}</div>
                <p className="text-sm text-stone-600 line-clamp-2">{s.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Reference desk — inline, context-aware. Native to the page, not a
          floating bubble. `context` is added to the first message so the
          assistant knows what the visitor was reading. */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <InlineAssistant tone="record" context={`Priority: ${p.title}`} />
      </section>

      <section className="bg-ccc-plum text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-3">Join the Network</div>
          <h2 className="font-serif text-4xl mb-4">Advocate for {p.title.toLowerCase()}.</h2>
          <p className="text-stone-300 max-w-2xl mx-auto mb-6">
            Get action alerts on this issue as they happen, and join Catholics across Colorado in making your voice heard at the Capitol.
          </p>
          <Link to="/take-action/join" className="inline-block bg-ccc-gold text-ccc-plum px-8 py-3 font-semibold uppercase tracking-wider text-sm hover:bg-ccc-gold-dark transition-colors">
            Join the Network
          </Link>
        </div>
      </section>
    </div>
  );
}
